import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader, PrimaryButton } from "@/components/admin/ui";
import { useFetch } from "@/hooks/useFetch";
import { api, errMsg, formatDateTime } from "@/lib/api";

function PublishDialog({ open, onOpenChange, latest, onDone }) {
  const [form, setForm] = useState({ title: "", body: "", material: true });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open && latest) setForm({ title: latest.title, body: latest.body, material: true }); }, [open, latest]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/consent-versions", form);
      toast.success(`Consent version ${data.version_number} published`);
      onOpenChange(false);
      onDone();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white rounded-2xl sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="publish-consent-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">Publish a new consent version</DialogTitle><DialogDescription>A material change requires every parent to re-accept before any new assessment.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2"><Label>Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="consent-version-title-input" /></div>
          <div className="space-y-2"><Label>Statement</Label><Textarea required rows={12} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="consent-version-body-input" /></div>
          <label className="flex items-center justify-between rounded-xl border border-[#E9DFE7] p-4"><span className="text-sm font-semibold">Material change (requires re-consent)</span><Switch checked={form.material} onCheckedChange={(v) => setForm({ ...form, material: v })} data-testid="consent-version-material-switch" /></label>
          <PrimaryButton type="submit" busy={busy} className="w-full" data-testid="consent-version-publish-button">Publish version</PrimaryButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ConsentVersions() {
  const { data, reload } = useFetch("/admin/consent-versions");
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="consent-versions-page">
      <PageHeader eyebrow="Compliance" title="Consent statement versions" description="The statement shown to parents is versioned. Each consent record stores the version accepted."
        actions={<PrimaryButton onClick={() => setOpen(true)} data-testid="new-consent-version-button"><Plus className="h-4 w-4" />New version</PrimaryButton>} />
      <div className="space-y-4">
        {data?.map((v, i) => (
          <details key={v.id} open={i === 0} className="card-surface p-6" data-testid={`consent-version-${v.version_number}`}>
            <summary className="cursor-pointer flex flex-wrap items-center gap-3">
              <span className="font-mono text-sm font-semibold text-[#4F0341]">v{v.version_number}</span>
              <span className="font-display font-bold">{v.title}</span>
              {i === 0 && <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-xs font-semibold text-emerald-700">Current</span>}
              {v.material && <span className="rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-800">Material</span>}
              <span className="text-xs text-[#8a7686] ml-auto">{formatDateTime(v.created_at)}</span>
            </summary>
            <p className="mt-4 text-sm leading-relaxed whitespace-pre-line text-[#3b2b38]">{v.body}</p>
          </details>
        ))}
      </div>
      <PublishDialog open={open} onOpenChange={setOpen} latest={data?.[0]} onDone={reload} />
    </div>
  );
}
